/**
 * Place a test callback without ringing anyone.
 *
 * This used to be a JSON textarea seeded with a sample payload. It worked, and it was
 * the wrong shape: Arun, looking at it — "let the user enter phone mnumber and conv
 * field values only - do the work that the uer doestn have to do and ask onlty what
 * they can tell you", and "this dry run should allow me to test all the agent channels
 * easily", and "fetch agent channels that have a clikc to call from 8loop and show
 * those".
 *
 * So the split is now explicit. What only a human can tell us — which channel, whose
 * number, what happened on the call, when they asked for, the values of this agent's
 * fields — is asked for. Everything else (the tenant domain, a unique call id, the
 * agent id and name behind the chosen channel, which payload key each context field
 * reads from, how a time gets encoded) is filled in here, because it is derivable and
 * asking for it was only ever a way to get it wrong.
 *
 * Two things kept from the old version because they earn their place:
 *
 *  - the exact payload is still shown, read-only, because the FDE's job is to write a
 *    code action that produces it. It is a target to copy, not a form to fill.
 *  - nothing is written and nobody is called. `POST /ingest/preview` runs the real
 *    ingest path — the same `parseRequestedTime`, `resolveSlot`, `resolveDialTarget`
 *    and `buildContext` — and stops before the insert.
 *
 * And one thing added: **Check every channel**, which runs the same preview once per
 * testable channel. The arun tenant has 23 dialable channels; "easily" cannot mean
 * choosing each one from a dropdown and pressing a button twenty-three times.
 */

import { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown, ChevronRight, ListChecks, Play, XCircle } from "lucide-react";
import { SettingsSection } from "@/components/ui/settings-section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  errorMessage,
  post,
  type AccountConfig,
  type AgentField,
  type FieldMapping,
  type PreviewResponse,
  type RoutedChannel,
} from "@/lib/api";
import { useAgentFields, useRouteResolution } from "@/lib/queries";

/** The verdicts `POST /ingest/preview` can return, coloured the way statuses are. */
const PREVIEW_TONE: Readonly<Record<string, string>> = {
  scheduled: "text-success",
  needs_attention: "text-destructive",
  invalid: "text-destructive",
  suppressed: "text-muted-foreground",
  outcome_disabled: "text-muted-foreground",
  not_a_callback: "text-muted-foreground",
};

/** Readable stand-ins, used only by the sweep where nobody typed a value. */
const PLACEHOLDERS: Readonly<Record<string, string>> = {
  first_name: "Priya",
  last_name: "Sharma",
  full_name: "Priya Sharma",
  contact_name: "Priya Sharma",
  borrower_name: "Priya Sharma",
  city: "Pune",
  pin_code: "411001",
  language: "Hindi",
};

/**
 * When they asked to be called back — the five shapes worth testing, one click each.
 *
 * These are not decoration: they are every branch of `parseRequestedTime`. An absolute
 * instant is the path the FDE guide asks for, a day-plus-part-of-day is what equentis's
 * `callback_date_and_time` actually emits, "nothing" exercises the account's default
 * delay, and free-form speech is the one that must land in needs-attention rather than
 * being guessed at — which is the single most surprising behaviour on this screen and
 * therefore the one that most deserves a button.
 */
const WHEN_PRESETS = [
  { value: "soon", label: "In about an hour", hint: "sent as an absolute requested_at" },
  {
    value: "relative",
    label: "“In twenty minutes”",
    hint: "sent as requested_time_raw “in 20 minutes” — the agent cannot know the clock, so it reports the offset and this app resolves it",
  },
  {
    value: "tomorrow_morning",
    label: "Tomorrow morning",
    hint: "sent as requested_time_raw “<date> morning”, the shape equentis emits",
  },
  { value: "exact", label: "A specific date and time", hint: "read in this account's timezone" },
  {
    value: "none",
    label: "They didn't name a time",
    hint: "no time sent at all — the “no time given” rule on the Outcomes tab decides whether this is dialled, and when",
  },
  {
    value: "unresolvable",
    label: "Something the agent couldn't resolve",
    hint: "free-form speech, which is deliberately never guessed at",
  },
] as const;

type WhenPreset = (typeof WHEN_PRESETS)[number]["value"];

type TestInput = {
  channelId: string;
  phone: string;
  contactName: string;
  outcome: string;
  when: WhenPreset;
  /** `datetime-local` value, only when `when === "exact"`. */
  exactAt: string;
  /** Keyed by the payload key a mapping reads, not by the target field name. */
  values: Record<string, string>;
};

export function DryRunSection({
  accountId,
  config,
}: {
  accountId: string;
  config: AccountConfig;
}) {
  const resolution = useRouteResolution(accountId);
  const agentFields = useAgentFields(accountId);

  const channels = resolution.data?.channels ?? [];
  const testable = channels.filter(isTestable);
  const untestable = channels.filter((c) => !isTestable(c));
  const channelsByAgent = groupChannelsByAgent(channels);

  const [input, setInput] = useState<Omit<TestInput, "channelId"> & { channelId: string | null }>({
    channelId: null,
    phone: "9568507001",
    contactName: "Test Contact",
    outcome: "callback_requested",
    when: "soon",
    exactAt: "",
    values: {},
  });

  // Null until the channel list arrives, then the first testable one — so the form is
  // usable on arrival rather than after a mandatory pick, and never seeds itself from a
  // made-up id that would report a fake failure.
  const channelId = input.channelId ?? testable[0]?.channel_id ?? null;
  const channel = channels.find((c) => c.channel_id === channelId) ?? null;
  const set = <K extends keyof TestInput>(k: K, v: TestInput[K]) =>
    setInput((prev) => ({ ...prev, [k]: v }));

  const fieldsByAgent = useMemo(() => byAgent(agentFields.data?.fields ?? []), [agentFields.data]);

  // What this channel would actually carry. Resolved exactly the way the server does it
  // at dial time — per-agent mapping wins over an account-wide one of the same name —
  // because a preview that resolves context differently from the dialler is worse than
  // no preview.
  const plan = useMemo(
    () =>
      contextPlan(
        config.field_mappings,
        channel?.dial.dial_agent_id ?? null,
        channel?.agent_id ?? null,
        fieldsByAgent,
      ),
    [config.field_mappings, channel?.dial.dial_agent_id, channel?.agent_id, fieldsByAgent],
  );

  const payload = useMemo(
    () =>
      channel
        ? buildPayload({ ...input, channelId: channel.channel_id }, channel, plan, config, false)
        : null,
    [input, channel, plan, config],
  );

  const [result, setResult] = useState<PreviewResponse | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [sweep, setSweep] = useState<SweepRow[] | null>(null);
  const [sweepProgress, setSweepProgress] = useState<number | null>(null);
  const [showPayload, setShowPayload] = useState(false);

  async function runOne() {
    if (!payload) return;
    setRunning(true);
    setRunError(null);
    setSweep(null);
    try {
      setResult(await post<PreviewResponse>(`/accounts/${accountId}/ingest/preview`, payload));
    } catch (err) {
      // A 422 body IS the answer here, not a failure — the server returns its verdict
      // with a non-2xx status when the payload would be rejected.
      setResult(null);
      setRunError(errorMessage(err));
    } finally {
      setRunning(false);
    }
  }

  /**
   * Every testable channel, one at a time.
   *
   * Sequential on purpose. This endpoint writes nothing, but it is the same handler the
   * live ingest uses and firing twenty-three concurrent requests at it to answer a
   * question nobody is waiting on is how a convenience becomes a load test.
   */
  async function runSweep() {
    setRunning(true);
    setSweep([]);
    setResult(null);
    setRunError(null);
    const rows: SweepRow[] = [];
    for (const [i, c] of testable.entries()) {
      setSweepProgress(i + 1);
      const p = contextPlan(config.field_mappings, c.dial.dial_agent_id, c.agent_id, fieldsByAgent);
      const body = buildPayload({ ...input, channelId: c.channel_id }, c, p, config, true);
      try {
        const r = await post<PreviewResponse>(`/accounts/${accountId}/ingest/preview`, body);
        rows.push({ channel: c, result: r.result, message: r.message, fields: r.details?.conversation_fields ?? null });
      } catch (err) {
        rows.push({ channel: c, result: "invalid", message: errorMessage(err), fields: null });
      }
      setSweep([...rows]);
    }
    setSweepProgress(null);
    setRunning(false);
  }

  const noChannels = !resolution.isLoading && testable.length === 0;

  return (
    <SettingsSection
      title="3. Prove a callback would be scheduled correctly"
      description="Post a test callback through the real ingest path and see exactly what would happen — when it would be dialled, on which channel, and with what context. Nothing is written and nobody is called."
      footer={
        <div className="flex flex-1 flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setShowPayload((v) => !v)}
            className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground underline decoration-dotted transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {showPayload ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
            {showPayload ? "Hide" : "Show"} the payload this sends
          </button>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={running || testable.length === 0}
              onClick={() => void runSweep()}
            >
              <ListChecks className="size-3.5" />
              {sweepProgress
                ? `Checking ${sweepProgress} of ${testable.length}…`
                : `Check all ${testable.length} channels`}
            </Button>
            <Button size="sm" disabled={running || !channel} onClick={() => void runOne()}>
              <Play className="size-3.5" />
              {running && sweepProgress === null ? "Running…" : "Run dry run"}
            </Button>
          </div>
        </div>
      }
    >
      {noChannels ? (
        <Alert variant="destructive">
          <XCircle className="size-4" />
          <AlertDescription>
            No channel on this tenant can place an outbound call, so there is nothing to test.
            {untestable.length > 0
              ? ` All ${untestable.length} known channel(s) are inbound-only, un-dialable or missing a routing identifier — the Routing tab says which.`
              : " Press Refresh channels on the Routing tab first."}
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="dry-channel">Which channel did the call come from?</Label>
            <Select
              value={channelId ?? ""}
              onValueChange={(v) => {
                set("channelId", v);
                setResult(null);
                setRunError(null);
              }}
            >
              <SelectTrigger id="dry-channel" className="w-full">
                <SelectValue placeholder={resolution.isLoading ? "Loading channels…" : "Choose a channel"} />
              </SelectTrigger>
              <SelectContent>
                {/*
                  Grouped by agent, per Arun: "in dry run can you gorup the agent chnanel by
                  agnets or somethign?" A flat list repeated each agent's name on every row —
                  "Retail · Clk 2 call inventory (176)", "Retail · (Primary) Inventory (162)" —
                  so the eye had to re-read the same word to find the one channel it wanted.
                  The agent is the thing you know; the channel is what you are choosing.
                */}
                {channelsByAgent.map(([agentName, list]) => (
                  <SelectGroup key={agentName}>
                    <SelectLabel>{agentName}</SelectLabel>
                    {list.map((c) => (
                      <SelectItem
                        key={c.channel_id}
                        value={c.channel_id}
                        disabled={!isTestable(c)}
                        className={isTestable(c) ? "cursor-pointer" : undefined}
                      >
                        {channelOnly(c)}
                        {isTestable(c) ? "" : ` — ${whyNotTestable(c)}`}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
            {channel && <ChannelNote channel={channel} />}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="dry-phone">Their phone number</Label>
              <Input
                id="dry-phone"
                value={input.phone}
                onChange={(e) => set("phone", e.target.value)}
                className="font-mono"
                inputMode="tel"
              />
              <p className="text-xs text-muted-foreground">
                {config.account.default_country_code
                  ? `A local number is read with this account's default country code (${config.account.default_country_code}). Send E.164 from a real hook.`
                  : "This account has no default country code, so anything but E.164 (+91…) is rejected."}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dry-name">Their name</Label>
              <Input
                id="dry-name"
                value={input.contactName}
                onChange={(e) => set("contactName", e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Shown in the queue, and available to context as “Contact name”.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dry-outcome">What happened on the call?</Label>
              <Select value={input.outcome} onValueChange={(v) => set("outcome", v)}>
                <SelectTrigger id="dry-outcome" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* `callback_no_time` is deliberately absent: no code action may post it.
                      It is the branch this app takes when a callback request arrives with no
                      time, and the way to test it is to pick "Callback requested" and then
                      "They didn't name a time" below. Offering it as a call ending would
                      invite a payload the ingest endpoint rejects.

                      `not_interested` and `dropped` are absent for the opposite reason: the
                      hook this app generates never posts them, so testing them would be
                      rehearsing a payload that cannot arrive — and the Outcomes tab has no row
                      for either, which is where the result would send the reader to disagree
                      with it. `dnc` and `wrong_number` DO stay: those really are posted, and
                      what they do (suppress the number for good) is worth seeing before it
                      happens to a live one. */}
                  {config.outcome_rules
                    .filter((r) => !UNTESTABLE.includes(r.outcome))
                    .map((r) => (
                      <SelectItem key={r.outcome} value={r.outcome}>
                        {r.label}
                        {r.never_dialled ? " — never dialled" : r.enabled ? "" : " — switched off"}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {outcomeNote(config, input.outcome)}
              </p>
            </div>
            {asksForTime(input.outcome) ? (
            <div className="space-y-1.5">
              <Label htmlFor="dry-when">When did they ask to be called?</Label>
              <Select value={input.when} onValueChange={(v) => set("when", v as WhenPreset)}>
                <SelectTrigger id="dry-when" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WHEN_PRESETS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {input.when === "exact" ? (
                <Input
                  type="datetime-local"
                  aria-label="Requested date and time"
                  value={input.exactAt}
                  onChange={(e) => set("exactAt", e.target.value)}
                />
              ) : null}
              <p className="text-xs text-muted-foreground">
                {WHEN_PRESETS.find((p) => p.value === input.when)?.hint}
              </p>
            </div>
            ) : (
              /* No question to ask. Said rather than left blank: an empty third of the row
                 reads as something failing to load, and the reader still wants to know what
                 decides the timing instead. */
              <div className="space-y-1.5">
                <p className="text-sm font-medium">When would it be dialled?</p>
                <p className="text-xs text-muted-foreground">
                  {config.outcome_rules.find((r) => r.outcome === input.outcome)?.never_dialled ? (
                    // No row on the Outcomes tab to point at, and nothing to time: these two
                    // block the number instead of scheduling a call.
                    <>
                      Nothing to ask, and nothing to time — this ending blocks the number instead
                      of scheduling a call.
                    </>
                  ) : (
                    <>
                      Nothing to ask — nobody on this call asked for a time, and a call that never
                      connected does not even run post-call analysis. The delay on the{" "}
                      {config.outcome_rules.find((r) => r.outcome === input.outcome)?.label ??
                        input.outcome}{" "}
                      row of the Outcomes tab decides it.
                    </>
                  )}
                </p>
              </div>
            )}
          </div>

          <ContextInputs
            plan={plan}
            values={input.values}
            agentName={channel?.agent_name ?? null}
            onChange={(key, v) => setInput((prev) => ({ ...prev, values: { ...prev.values, [key]: v } }))}
          />

          {showPayload && payload && (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">
                What your <code className="font-mono">POST_CONVERSATION</code> code action has to
                produce. The values you typed are nested under{" "}
                <code className="font-mono">context</code>; a hook that forwards its whole
                conversation-field map can send them flat at the top level instead — both are read.
              </p>
              <pre className="max-h-72 overflow-auto rounded-md bg-muted p-3 font-mono text-[11px] leading-relaxed">
                {JSON.stringify(payload, null, 2)}
              </pre>
            </div>
          )}

          {runError && (
            <Alert variant="destructive">
              <XCircle className="size-4" />
              <AlertDescription>{runError}</AlertDescription>
            </Alert>
          )}

          {result && (
            <div className="space-y-3 rounded-lg border border-border p-4">
              <p className={cn("text-sm font-medium", PREVIEW_TONE[result.result])}>
                Would {result.would}
              </p>
              <p className="text-sm">{result.message}</p>
              {result.details && (
                <pre className="max-h-64 overflow-auto rounded-md bg-muted p-3 font-mono text-[11px] leading-relaxed">
                  {JSON.stringify(result.details, null, 2)}
                </pre>
              )}
            </div>
          )}

          {sweep && (
            <SweepTable
              rows={sweep}
              total={testable.length}
              what={`the same number, “${
                config.outcome_rules.find((r) => r.outcome === input.outcome)?.label ?? input.outcome
              }” and “${WHEN_PRESETS.find((w) => w.value === input.when)?.label}”`}
            />
          )}
        </>
      )}
    </SettingsSection>
  );
}

// ------------------------------------------------------------ context inputs ---

/**
 * One input per context field this channel would carry — labelled by the field on the
 * dialled agent, not by the payload key underneath it.
 *
 * The fields we fill ourselves are listed too, greyed. Without them the screen answers
 * "what do I have to type" but not "what will the callback know", and the second is the
 * question somebody opened this tab with.
 */
function ContextInputs({
  plan,
  values,
  agentName,
  onChange,
}: {
  plan: PlannedField[];
  values: Record<string, string>;
  agentName: string | null;
  onChange: (payloadKey: string, value: string) => void;
}) {
  const asked = plan.filter((p) => p.mapping.source_kind === "payload");
  const derived = plan.filter((p) => p.mapping.source_kind !== "payload");

  if (plan.length === 0) {
    return (
      <p className="text-xs text-warning">
        No context field is enabled for this agent, so the callback would open cold —{" "}
        <code className="font-mono">conversation_fields</code> comes back empty. Add one on the
        Context tab.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <p className="text-xs font-medium text-foreground">
          Context {agentName ? `${agentName} would receive` : "the callback would receive"}
        </p>
        <p className="text-xs text-muted-foreground">
          {plan.length} field{plan.length === 1 ? "" : "s"}
          {asked.length > 0
            ? `, ${asked.length} of them from the payload — leave one blank to see what happens when the hook forgets it`
            : ", all filled by us"}
        </p>
      </div>

      {asked.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {asked.map((p) => (
            <div key={p.mapping.field_name} className="space-y-1.5">
              <Label htmlFor={`ctx-${p.mapping.field_name}`} className="font-mono text-xs">
                {p.mapping.field_name}
              </Label>
              {p.source?.allowed_values.length ? (
                <Select
                  value={values[p.mapping.source_value] ?? ""}
                  onValueChange={(v) => onChange(p.mapping.source_value, v)}
                >
                  <SelectTrigger id={`ctx-${p.mapping.field_name}`} className="w-full">
                    <SelectValue placeholder="Choose a value" />
                  </SelectTrigger>
                  <SelectContent>
                    {p.source.allowed_values.map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={`ctx-${p.mapping.field_name}`}
                  value={values[p.mapping.source_value] ?? ""}
                  placeholder={PLACEHOLDERS[p.mapping.source_value] ?? ""}
                  inputMode={isNumeric(p.source?.field_type) ? "numeric" : undefined}
                  onChange={(e) => onChange(p.mapping.source_value, e.target.value)}
                />
              )}
              <p className="text-xs text-muted-foreground">
                {p.source?.description ??
                  `reads the payload's "${p.mapping.source_value}"${
                    p.source ? "" : " — no agent on this account declares a field by that name"
                  }`}
              </p>
            </div>
          ))}
        </div>
      )}

      {derived.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {derived.map((p) => (
            <Badge key={p.mapping.field_name} variant="secondary" className="gap-1 font-normal">
              <span className="font-mono text-[11px]">{p.mapping.field_name}</span>
              <span className="text-[11px] text-muted-foreground">
                {p.mapping.source_kind === "literal"
                  ? `always “${p.mapping.source_value}”`
                  : "we work it out"}
              </span>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------- sweep ---

type SweepRow = {
  channel: RoutedChannel;
  result: string;
  message: string;
  fields: Record<string, string> | null;
};

function SweepTable({
  rows,
  total,
  what,
}: {
  rows: SweepRow[];
  total: number;
  what: string;
}) {
  const ok = rows.filter((r) => r.result === "scheduled").length;
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {ok} of {rows.length}
        {rows.length < total ? ` (of ${total})` : ""} channel(s) would schedule a callback.
        {/*
          Naming the inputs matters here in a way it does not for a single run: a sweep
          reading "0 of 10 would schedule" is alarming until you remember you had left
          the time preset on the one that deliberately cannot be resolved.
        */}{" "}
        Every channel was sent {what}. Every payload value you left blank was filled with a
        stand-in, so “no context” always means no mapping.
      </p>
      <div className="divide-y divide-border rounded-lg border border-border">
        {rows.map((r) => (
          <div key={r.channel.channel_id} className="flex items-start gap-3 px-3 py-2">
            <span
              className={cn(
                "mt-0.5 w-28 shrink-0 text-xs font-medium",
                PREVIEW_TONE[r.result] ?? "",
              )}
            >
              {r.result.replace(/_/g, " ")}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-foreground">{channelLabel(r.channel)}</p>
              <p className="text-xs text-muted-foreground">{r.message}</p>
              {r.fields && Object.keys(r.fields).length === 0 && (
                <p className="flex items-start gap-1.5 text-xs text-warning">
                  <AlertTriangle className="mt-0.5 size-3 shrink-0" />
                  <span>would carry no context — this agent has no enabled context fields</span>
                </p>
              )}
              {r.fields && Object.keys(r.fields).length > 0 && (
                <p className="font-mono text-[11px] text-muted-foreground">
                  {Object.keys(r.fields).join(" · ")}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ helpers ---

/**
 * Can this channel actually source a callback we could dial back on?
 *
 * Arun asked for "agent channels that have a clikc to call from 8loop" — which is what
 * the app's own `dialable` verdict already means: an OUTBOUND, AGENT-initiated channel
 * with a routing identifier and a telephony integration behind it. `dialable === null`
 * counts as testable rather than not, because null means the key could not read phone
 * numbers, i.e. unchecked — calling that un-dialable would hide a working channel.
 */
function isTestable(c: RoutedChannel): boolean {
  return c.live && !c.inbound_only && c.dial.how !== "unroutable" && c.dial.dialable !== false;
}

function whyNotTestable(c: RoutedChannel): string {
  if (!c.live) return "not in the current channel list";
  if (c.inbound_only) return "inbound only, so it never starts a call";
  if (c.dial.how === "unroutable") return c.dial.unroutable_reason ?? "nothing to dial";
  return c.dial.blocked_reason ?? "cannot be dialled";
}

function channelLabel(c: RoutedChannel): string {
  const name = c.agent_name ?? "Unnamed agent";
  const label = c.channel_label && c.channel_label !== c.agent_name ? ` · ${c.channel_label}` : "";
  return `${name}${label} (${c.channel_id})`;
}

/** The channel alone, for a list already grouped under its agent's name. */
function channelOnly(c: RoutedChannel): string {
  return `${c.channel_label ?? "unnamed channel"} (${c.channel_id})`;
}

/**
 * Channels grouped under their agent, testable ones first inside each group.
 *
 * A group with nothing testable in it sinks to the bottom rather than disappearing: "where is
 * channel 288?" is the first question a missing row provokes, and the answer — inbound-only, or
 * no routing identifier — is the actual finding.
 */
function groupChannelsByAgent(channels: RoutedChannel[]): [string, RoutedChannel[]][] {
  const groups = new Map<string, RoutedChannel[]>();
  for (const c of channels) {
    const name = c.agent_name ?? `Agent ${c.agent_id ?? "?"}`;
    const list = groups.get(name);
    if (list) list.push(c);
    else groups.set(name, [c]);
  }
  return [...groups.entries()]
    .map(
      ([name, list]) =>
        [name, [...list].sort((a, b) => Number(isTestable(b)) - Number(isTestable(a)))] as [
          string,
          RoutedChannel[],
        ],
    )
    .sort(
      (a, b) =>
        Number(b[1].some(isTestable)) - Number(a[1].some(isTestable)) ||
        a[0].localeCompare(b[0]),
    );
}

function ChannelNote({ channel }: { channel: RoutedChannel }) {
  return (
    <div className="space-y-1">
      {/* The agent, because the picker's rows are grouped under it and the closed trigger
          therefore shows the channel alone. Losing which agent you chose is not an
          acceptable price for a tidier list. */}
      <p className="text-xs text-muted-foreground">
        On <span className="text-foreground">{channel.agent_name ?? `agent ${channel.agent_id ?? "?"}`}</span>.
      </p>
      <p className="text-xs text-muted-foreground">
        {channel.dial.how === "override"
          ? `Overridden on the Routing tab — the callback dials ${channel.dial.channel_label ?? channel.dial.routing_identifier} instead of coming back on this channel.`
          : "Dials back on this same channel, which is the default and needs no configuration."}
        {channel.dial.caller_id ? ` They would see ${channel.dial.caller_id}.` : ""}
      </p>
      {channel.dial.warnings.map((w) => (
        <p key={w} className="flex items-start gap-1.5 text-xs text-warning">
          <AlertTriangle className="mt-0.5 size-3 shrink-0" />
          <span>{w}</span>
        </p>
      ))}
    </div>
  );
}

/**
 * Could this outcome have carried a requested time at all?
 *
 * Only a callback request can. Everything else either never connected (voicemail, no answer —
 * no post-call analysis runs, so no field is written) or connected without anyone asking for
 * a call back. Asking "when did they ask to be called?" about those is asking about something
 * that did not happen.
 */
function asksForTime(outcome: string): boolean {
  return outcome === "callback_requested";
}

/**
 * Rule slugs this dry run does not offer as a call ending.
 *
 * `callback_no_time` because nothing may post it — it is a branch, reached by picking a callback
 * request and then "They didn't name a time". `not_interested` and `dropped` because the hook
 * this app generates never posts them either: a call this app never hears about is a call it
 * cannot make, so both left the Outcomes tab too, and a dry run for one would explain its result
 * by pointing at a row that is not there.
 */
const UNTESTABLE: readonly string[] = ["callback_no_time", "not_interested", "dropped"];

function outcomeNote(config: AccountConfig, outcome: string): string {
  const rule = config.outcome_rules.find((r) => r.outcome === outcome);
  if (!rule) return "";
  if (rule.never_dialled)
    return "This outcome is never dialled — the dry run should report that it would be suppressed, and the number added to Do not call.";
  if (!rule.enabled)
    return "Switched off on the Outcomes tab, so the dry run should report that nothing would be scheduled. That is the point of testing it.";
  // A callback request is dialled at the time it named, and the "no time given" rule covers the
  // case where it named none — so quoting a delay here would describe neither.
  if (asksForTime(outcome)) {
    const noTime = config.outcome_rules.find((r) => r.outcome === "callback_no_time");
    return noTime && !noTime.enabled
      ? "Dialled at the time they asked for. If no time was captured, this account records the call and never dials it — the “no time given” row on the Outcomes tab."
      : `Dialled at the time they asked for, or ${
          noTime?.delay_minutes ?? noTime?.suggested_delay_minutes ?? 120
        } minute(s) after the call when no time was captured.`;
  }
  return `Scheduled ${rule.delay_minutes ?? rule.suggested_delay_minutes} minute(s) after the call — nobody on this call named a time.`;
}

type PlannedField = { mapping: FieldMapping; source: AgentField | null };

function byAgent(fields: AgentField[]): Map<string, AgentField[]> {
  const m = new Map<string, AgentField[]>();
  for (const f of fields) m.set(f.agent_id, [...(m.get(f.agent_id) ?? []), f]);
  return m;
}

/**
 * The context this channel would carry, resolved the way the server resolves it.
 *
 * Mirrors `listEnabledFieldMappings`: enabled only, account-wide or this agent, and a
 * per-agent row beats an account-wide row of the same name. `source` is the *source*
 * agent's own field of that name — which is where the type and the allowed values for
 * the input come from, since a `payload` mapping reads a name the previous call's
 * post-call analysis wrote.
 */
function contextPlan(
  mappings: FieldMapping[],
  /**
   * The agent that will ANSWER. It decides which mappings apply and which field names are
   * even settable, because a conversation field only accepts a value at call start on the
   * agent that declares it — resolve against the wrong one and 8loop refuses the whole
   * call. Matches `dialAgentFor` on the server; the preview would otherwise promise a set
   * of fields the dialler does not send.
   */
  dialAgentId: string | null,
  /**
   * The agent whose call is being simulated. Different agent, different question: a
   * `payload` mapping reads a name the PREVIOUS call's post-call analysis wrote, so the
   * type and allowed values for its input come from the source agent's own fields.
   */
  sourceAgentId: string | null,
  fieldsByAgent: Map<string, AgentField[]>,
): PlannedField[] {
  const applicable = mappings.filter(
    (m) => m.enabled && (m.agent_id === null || m.agent_id === dialAgentId),
  );
  const byName = new Map<string, FieldMapping>();
  for (const m of applicable) {
    const existing = byName.get(m.field_name);
    if (!existing || (existing.agent_id === null && m.agent_id !== null)) byName.set(m.field_name, m);
  }
  const agentFields = sourceAgentId ? (fieldsByAgent.get(sourceAgentId) ?? []) : [];
  return [...byName.values()]
    .sort((a, b) => a.field_name.localeCompare(b.field_name))
    .map((mapping) => ({
      mapping,
      source:
        mapping.source_kind === "payload"
          ? (agentFields.find((f) => f.field_name === mapping.source_value) ?? null)
          : null,
    }));
}

function isNumeric(type: string | null | undefined): boolean {
  return type === "INTEGER" || type === "FLOAT";
}

/**
 * Assemble the payload the FDE's code action would send.
 *
 * Everything derivable is derived here. `source_call_id` is fresh on every build because
 * it is the idempotency key: reuse it and the second dry run is correctly reported as a
 * duplicate of the first, which reads exactly like a broken screen.
 */
function buildPayload(
  input: TestInput,
  channel: RoutedChannel,
  plan: PlannedField[],
  config: AccountConfig,
  /**
   * True only for the sweep. A blank box means "test the code action forgetting this"
   * on the single-channel run, which is the whole reason it is allowed to be blank; but
   * across twenty-three channels it would make every missing value indistinguishable
   * from a missing *mapping*, and "would carry no context" has to mean one thing.
   */
  fillBlanks: boolean,
): Record<string, unknown> {
  const context: Record<string, string> = {};
  for (const p of plan) {
    if (p.mapping.source_kind !== "payload") continue;
    const key = p.mapping.source_value;
    const typed = input.values[key]?.trim();
    const value =
      typed || (fillBlanks ? (PLACEHOLDERS[key] ?? `sample ${key}`) : "");
    if (value) context[key] = value;
  }

  const when = encodeWhen(input);

  return {
    tenant_domain: config.account.tenant_host,
    source_call_id: `dry-run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    phone_number: input.phone.trim(),
    contact_name: input.contactName.trim() || undefined,
    outcome: input.outcome,
    agent_id: channel.agent_id ?? undefined,
    agent_name: channel.agent_name ?? undefined,
    agent_channel_id: channel.channel_id,
    ...when,
    ...(Object.keys(context).length > 0 ? { context } : {}),
  };
}

/**
 * The time to send, if any.
 *
 * Nothing at all unless the outcome is a callback request. Arun, looking at a voicemail dry
 * run: *"for dry run, why will voicemail have when did they ask to be called?"* Quite — a call
 * that reached an answering machine never asked for anything, and it never even ran post-call
 * analysis, so no time could exist. The screen used to keep whatever was in the box and send
 * it anyway, which parked a perfectly ordinary voicemail in "needs a human" for a phrase
 * nobody on that call had said.
 */
function encodeWhen(input: TestInput): Record<string, string> {
  if (!asksForTime(input.outcome)) return {};
  switch (input.when) {
    case "soon": {
      const d = new Date(Date.now() + 60 * 60 * 1000);
      d.setMinutes(0, 0, 0);
      return { requested_at: d.toISOString() };
    }
    case "tomorrow_morning": {
      const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return { requested_time_raw: `${iso} morning` };
    }
    case "exact":
      // A bare local time with no offset, read in the account's timezone — which is
      // what `datetime-local` gives us and what the server documents.
      return input.exactAt ? { requested_at: input.exactAt } : {};
    case "relative":
      return { requested_time_raw: "in 20 minutes" };
    case "none":
      return { requested_time_raw: "requested no time given" };
    case "unresolvable":
      return { requested_time_raw: "kal shaam 5 baje" };
  }
}
